import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { FolderState, Folder, BackendFolder } from "@dataRoomSetup/models";
import {
  getDataRoomItemKey,
  parseDataRoomItemKey,
  VIDEO_DOCUMENT_ID_PLACEHOLDER,
} from "@dataRoomSetup/utils/dataRoomItemKey";
import {
  fetchInitialFoldersThunk,
  fetchInitialFoldersListThunk,
  postDataRoomsetFoldersThunk,
} from "./fetchInitialFoldersDuck";

export interface DataRoomSetupFolderState extends FolderState {
  folders: Folder[];
  rawFolders: Folder[];
  dataroomFolderList: string[];
  loading: boolean;
  error: string | null;
  selectedSourceFolderIds: number[];
  selectedFolderCounts: Record<number, number>;
}

let tempIdCounter = -1;
const generateTempId = (): number => {
  return tempIdCounter--;
};

const initialState: DataRoomSetupFolderState = {
  folders: [],
  rawFolders: [],
  dataroomFolderList: [],
  loading: false,
  error: null,
  selectedFolderId: null,
  pendingChanges: [],
  selectedFiles: [],
  selectedFilesSourceFolder: null,
  selectedSourceFolderIds: [],
  selectedFolderCounts: {},
  expandedFolders: [],
  isAddFolderDialogOpen: false,
  ui: { showModal: false, rootReorderFolderId: undefined } as any,
};

const folderSlice = createSlice({
  name: "folders",
  initialState,
  reducers: {
    startRootReorder(state, action: PayloadAction<{ folderId: number }>) {
      (state.ui as any).rootReorderFolderId = action.payload.folderId;
    },
    stopRootReorder(state) {
      (state.ui as any).rootReorderFolderId = undefined;
    },
    moveRootUp(state, action: PayloadAction<{ folderId: number }>) {
      const id = action.payload.folderId;
      const idx = state.folders.findIndex((f) => f.parentId === null && f.detailId === id);

      if (idx > 0) {
        const tmp = state.folders[idx - 1];

        state.folders[idx - 1] = state.folders[idx];
        state.folders[idx] = tmp as any;

        // Recompute sortOrder for all root folders
        let order = 1;

        for (const f of state.folders) {
          if (f.parentId === null) {
            (f as any).sortOrder = order++;
          }
        }

        state.pendingChanges.push({ type: "reorderRoot", folderId: id, direction: "up" });
      }
    },
    moveRootDown(state, action: PayloadAction<{ folderId: number }>) {
      const id = action.payload.folderId;
      const roots = state.folders.filter((f) => f.parentId === null);
      const idxInRoots = roots.findIndex((f) => f.detailId === id);

      if (idxInRoots >= 0 && idxInRoots < roots.length - 1) {
        // Map root indices to overall array positions
        const rootPositions: number[] = [];

        state.folders.forEach((f, i) => {
          if (f.parentId === null) rootPositions.push(i);
        });

        const a = rootPositions[idxInRoots];
        const b = rootPositions[idxInRoots + 1];

        const tmp = state.folders[a];

        state.folders[a] = state.folders[b];
        state.folders[b] = tmp as any;

        // Recompute sortOrder for all root folders
        let order = 1;

        for (const f of state.folders) {
          if (f.parentId === null) {
            (f as any).sortOrder = order++;
          }
        }

        state.pendingChanges.push({ type: "reorderRoot", folderId: id, direction: "down" });
      }
    },
    addFolder(state, action: PayloadAction<{ parentId: number | null; folderName: string }>) {
      if (action.payload.parentId === null) {
        const currentMaxOrder = state.folders
          .filter((f) => f.parentId === null)
          .reduce((m, f) => Math.max(m, (f as any).sortOrder || 0), 0);
        const tempId = generateTempId();
        const newFolder: Folder = {
          headerId: tempId,
          detailId: tempId,
          parentId: null,
          folderName: action.payload.folderName,
          documentTypeId: 0,
          deletionFlag: false,
          systemPreferences: false,
          isDisplay: true,
          sortOrder: currentMaxOrder + 1,
          children: [],
          documents: [],
          isSystem: false,
          isExpanded: true,
          unsaved: true,
        };

        state.folders.push(newFolder);
        state.pendingChanges.push({
          type: "addFolder",
          payload: action.payload,
        });

        return;
      }
      const findFolder = (folders: BackendFolder[], detailId: number): BackendFolder | null => {
        for (const folder of folders) {
          if (folder.detailId === detailId) return folder;
          const found = findFolder(folder.children, detailId);

          if (found) return found;
        }

        return null;
      };
      const parent = findFolder(state.folders, action.payload.parentId);

      if (parent) {
        const tempDetailId = generateTempId();
        const currentMaxChildOrder = (parent.children || []).reduce(
          (m, c) => Math.max(m, ((c as any).sortOrder as number) || 0),
          0
        );

        parent.children.push({
          headerId: parent.headerId,
          detailId: tempDetailId,
          parentId: parent.detailId,
          folderName: action.payload.folderName,
          documentTypeId: 0,
          deletionFlag: false,
          systemPreferences: false,
          isDisplay: true,
          sortOrder: currentMaxChildOrder + 1,
          children: [],
          documents: [],
          isSystem: false,
          isExpanded: true,
          unsaved: true,
        });
        state.pendingChanges.push({
          type: "addSubfolder",
          payload: action.payload,
        });
      }
    },
    renameFolder(
      state,
      action: PayloadAction<{
        folderId: number;
        newName: string;
        oldPath?: string;
        newPath?: string;
        affectedDocumentIds?: string[];
      }>
    ) {
      const findFolder = (folders: BackendFolder[], id: number): Folder | null => {
        for (const folder of folders) {
          if (folder.detailId === id) return folder;
          const found = findFolder(folder.children, id);

          if (found) return found;
        }

        return null;
      };
      const folder = findFolder(state.folders, action.payload.folderId);

      if (folder) {
        const oldName = folder.folderName;

        // Update folder name immediately in UI
        folder.folderName = action.payload.newName;

        // Mark as unsaved for UI indication (yellow background)
        folder.unsaved = true;

        // Add to pending changes for batch API call on "Save Changes"
        state.pendingChanges.push({
          type: "renameFolder",
          folderId: action.payload.folderId,
          oldName,
          newName: action.payload.newName,
          oldPath: action.payload.oldPath,
          newPath: action.payload.newPath,
          affectedDocumentIds: action.payload.affectedDocumentIds,
          payload: action.payload,
        });
      }

      // Clear renaming state
      state.ui.renamingFolderId = undefined;
    },
    setRenamingFolder(state, action: PayloadAction<{ folderId: number | undefined }>) {
      state.ui.renamingFolderId = action.payload.folderId;
    },
    deleteFolder(
      state,
      action: PayloadAction<{
        folderId: number;
        systemFolderId: number | undefined;
        documentsToMove?: Array<{
          itemKey: string;
          documentId: string;
          videoId?: string;
          targetFolderId: number;
          targetFolderPath: string;
        }>;
        deletedItemKeys?: string[];
        videoIdByDocumentId?: Record<string, string>;
      }>
    ) {
      const { folderId, documentsToMove, videoIdByDocumentId = {} } = action.payload;

      // Find any folder in the tree by ID
      const findFolder = (folders: BackendFolder[], id: number): Folder | null => {
        for (const folder of folders) {
          if (folder.detailId === id) return folder;
          const found = findFolder(folder.children, id);

          if (found) return found;
        }

        return null;
      };

      // Completely remove folder from state
      const removeFolder = (folders: BackendFolder[], targetId: number): boolean => {
        for (let i = 0; i < folders.length; i++) {
          if (folders[i].detailId === targetId) {
            folders.splice(i, 1);

            return true;
          }
          if (folders[i].children?.length && removeFolder(folders[i].children, targetId)) {
            return true;
          }
        }

        return false;
      };

      // Mark folder and all nested children for deletion
      const markAsDeleted = (folder: Folder) => {
        folder.deletionFlag = true;
        // Keep documents array intact - backend needs it to relocate documents
        folder.children?.forEach(markAsDeleted);
      };

      const folder = findFolder(state.folders, folderId);

      if (!folder) return;

      if (
        (documentsToMove && documentsToMove.length > 0) ||
        (action.payload.deletedItemKeys && action.payload.deletedItemKeys.length > 0)
      ) {
        const keysToMarkDeleted = new Set(
          (action.payload.deletedItemKeys && action.payload.deletedItemKeys.length > 0
            ? action.payload.deletedItemKeys
            : (documentsToMove || []).map((d) => d.itemKey)
          ).map((key) => key + "")
        );

        const collectSubtreeFolders = (root: Folder): Folder[] => {
          const result: Folder[] = [root];

          if (root.children && root.children.length > 0) {
            for (const child of root.children as any) {
              result.push(...collectSubtreeFolders(child as Folder));
            }
          }

          return result;
        };

        const subtreeFolders = collectSubtreeFolders(folder);

        const movesByTarget = new Map<
          number,
          Array<{
            itemKey: string;
            documentId: string;
            videoId?: string;
            targetFolderId: number;
            targetFolderPath: string;
          }>
        >();

        if (documentsToMove && documentsToMove.length > 0) {
          for (const move of documentsToMove) {
            if (!movesByTarget.has(move.targetFolderId)) {
              movesByTarget.set(move.targetFolderId, []);
            }

            movesByTarget.get(move.targetFolderId)!.push(move);
          }
        }

        for (const f of subtreeFolders) {
          if (!f.documents) {
            f.documents = [] as any;
          }

          const nextDocs: any[] = [];

          for (const doc of f.documents || []) {
            const shouldDelete = keysToMarkDeleted.has(getDataRoomItemKey(doc, videoIdByDocumentId));
            const isSavedEntry = !!(doc && typeof doc === "object" && ("id" in doc || "folderDetailId" in doc));

            if (shouldDelete) {
              if (isSavedEntry) {
                const savedDoc = doc as {
                  id: number | string;
                  folderDetailId: number | string;
                  documentId: string;
                  documentDeletionFlag: boolean;
                };

                nextDocs.push({
                  ...savedDoc,
                  folderDetailId: savedDoc.folderDetailId ?? f.detailId,
                  documentDeletionFlag: true,
                });
              }
              // If not saved, drop it entirely
            } else {
              nextDocs.push(doc);
            }
          }

          f.documents = nextDocs;
        }

        // 2) Add/activate documents in each target folder (only where a target exists)
        if (documentsToMove && documentsToMove.length > 0) {
          for (const [targetFolderId, moves] of Array.from(movesByTarget.entries())) {
            const targetFolder = findFolder(state.folders, targetFolderId);

            if (!targetFolder) continue;

            if (!targetFolder.documents) targetFolder.documents = [] as any;

            for (const move of moves) {
              let foundInTarget = false;

              targetFolder.documents = (targetFolder.documents || []).map((doc: any) => {
                if (getDataRoomItemKey(doc, videoIdByDocumentId) === move.itemKey) {
                  foundInTarget = true;

                  return {
                    ...doc,
                    documentDeletionFlag: false,
                    ...(move.videoId && !doc.videoId ? { videoId: move.videoId } : {}),
                  };
                }

                return doc;
              });

              if (!foundInTarget) {
                targetFolder.documents = [
                  ...targetFolder.documents,
                  {
                    documentId: move.documentId,
                    ...(move.videoId ? { videoId: move.videoId } : {}),
                    documentDeletionFlag: false,
                  },
                ];
              }
            }

            state.pendingChanges.push({
              type: "moveFilesOnDelete",
              folderId,
              targetFolderId,
              itemKeys: moves.map((m) => m.itemKey),
            });
          }
        }
      }

      if (folder.unsaved && !folder.isSystem) {
        // New unsaved folder - remove completely
        removeFolder(state.folders, folderId);
        state.pendingChanges = state.pendingChanges.filter(
          (c) => !((c.type === "addFolder" || c.type === "addSubfolder") && c.payload.folderName === folder.folderName)
        );
      } else {
        // Existing folder - mark for deletion
        markAsDeleted(folder);
        state.pendingChanges.push({ type: "deleteFolder", payload: action.payload });
      }
    },
    toggleExpandFolder(state, action: PayloadAction<{ folderId: number }>) {
      if (state.expandedFolders.includes(action.payload.folderId)) {
        state.expandedFolders = state.expandedFolders.filter((detailId) => detailId !== action.payload.folderId);
      } else {
        state.expandedFolders.push(action.payload.folderId);
      }
    },
    collapseAllFolders(state) {
      state.expandedFolders = [];
    },
    selectFiles(state, action: PayloadAction<{ fileIds: string[]; sourceFolderId: number }>) {
      state.selectedFiles = action.payload.fileIds;
      state.selectedFilesSourceFolder = action.payload.sourceFolderId;
    },
    incrementSelectionForFolder(state, action: PayloadAction<{ folderId: number }>) {
      const id = action.payload.folderId;
      const counts: Record<number, number> = state.selectedFolderCounts as any;

      counts[id] = (counts[id] || 0) + 1;
      state.selectedFolderCounts = counts as any;
      if (!state.selectedSourceFolderIds.includes(id)) state.selectedSourceFolderIds.push(id);
    },
    decrementSelectionForFolder(state, action: PayloadAction<{ folderId: number }>) {
      const id = action.payload.folderId;
      const counts: Record<number, number> = state.selectedFolderCounts as any;
      const next = Math.max(0, (counts[id] || 0) - 1);

      counts[id] = next;
      state.selectedFolderCounts = counts as any;
      if (next === 0) {
        state.selectedSourceFolderIds = state.selectedSourceFolderIds.filter((x: number) => x !== id);
      }
    },
    clearSelection(state) {
      state.selectedFiles = [];
      state.selectedFilesSourceFolder = null;
      state.selectedSourceFolderIds = [];
      state.selectedFolderCounts = {} as any;
    },
    moveSelectedFiles(
      state,
      action: PayloadAction<{ targetFolderId: number; videoIdByDocumentId?: Record<string, string> }>
    ) {
      const { targetFolderId, videoIdByDocumentId = {} } = action.payload;
      const sourceFolderId = state.selectedFilesSourceFolder;

      if (!sourceFolderId || !state.selectedFiles.length) return;

      const findFolder = (folders: BackendFolder[], id: number): Folder | null => {
        for (const folder of folders) {
          if (folder.detailId === id) return folder;
          const found = findFolder(folder.children, id);

          if (found) return found;
        }

        return null;
      };
      const sourceFolder = findFolder(state.folders, sourceFolderId);
      const targetFolder = findFolder(state.folders, targetFolderId);

      if (sourceFolder && targetFolder) {
        // Ensure docs arrays exist
        if (!sourceFolder.documents) sourceFolder.documents = [] as any;
        if (!targetFolder.documents) targetFolder.documents = [] as any;
        // Normalize ids to strings for reliable comparison
        const idsToMove = state.selectedFiles.map((id) => id + "");
        const idsSet = new Set(idsToMove);

        // 1) Mark the documents as deleted in the source folder ONLY if the saved entry exists (has id/folderDetailId)
        if (sourceFolder.detailId !== targetFolderId) {
          const nextDocs: any[] = [];

          for (const doc of sourceFolder.documents || []) {
            const entryKey = getDataRoomItemKey(doc, videoIdByDocumentId);
            const isMoved = idsSet.has(entryKey);
            const isSavedEntry = !!(doc && typeof doc === "object" && ("id" in doc || "folderDetailId" in doc));

            if (isMoved) {
              if (isSavedEntry) {
                // Keep a deletion record only for previously saved associations
                const savedDoc = doc as {
                  id: number | string;
                  folderDetailId: number | string;
                  documentId: string;
                  documentDeletionFlag: boolean;
                };

                nextDocs.push({
                  ...savedDoc,
                  folderDetailId: savedDoc.folderDetailId ?? sourceFolder.detailId,
                  documentDeletionFlag: true,
                });
              }
              // If not saved, drop it entirely (do not create a deletion record)
            } else {
              nextDocs.push(doc);
            }
          }

          sourceFolder.documents = nextDocs;
        }

        // 2) Remove any previously moved active entries from ALL folders except the final target

        const removeActiveFromAllExceptTarget = (folders: BackendFolder[]) => {
          for (const f of folders) {
            if (f.detailId !== targetFolderId) {
              f.documents = (f.documents || []).filter((d: any) => {
                const entryKey = getDataRoomItemKey(d, videoIdByDocumentId);

                // Keep if different doc OR it's a deleted (source) record
                return !idsSet.has(entryKey) || d.documentDeletionFlag === true;
              });
            }

            if (f.children && f.children.length) removeActiveFromAllExceptTarget(f.children as any);
          }
        };

        removeActiveFromAllExceptTarget(state.folders as any);

        // 3) Ensure documents are active (not deleted) in the final target folder
        for (const docId of idsToMove) {
          let foundInTarget = false;
          const parsed = parseDataRoomItemKey(docId);
          const resolvedVideoId = parsed.videoId || videoIdByDocumentId[parsed.documentId];

          targetFolder.documents = (targetFolder.documents || []).map((doc: any) => {
            if (getDataRoomItemKey(doc, videoIdByDocumentId) === docId) {
              foundInTarget = true;

              return {
                ...doc,
                documentDeletionFlag: false,
                ...(resolvedVideoId && !doc.videoId ? { videoId: resolvedVideoId } : {}),
              };
            }

            return doc;
          });

          if (!foundInTarget) {
            targetFolder.documents = [
              ...targetFolder.documents,
              {
                documentId: resolvedVideoId ? VIDEO_DOCUMENT_ID_PLACEHOLDER : parsed.documentId,
                ...(resolvedVideoId ? { videoId: resolvedVideoId } : {}),
                documentDeletionFlag: false,
              },
            ];
          }
        }

        state.selectedFiles = [];
        state.selectedFilesSourceFolder = null;
        state.pendingChanges.push({
          type: "moveFiles",
          payload: action.payload,
        });
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    assignFile(state, action: PayloadAction<{ fileId: string; folderId: number }>) {
      // Implementation for assigning file
      // console.log(state, action);
    },
    saveChanges(state) {
      // console.log(state);
      const clearUnsaved = (folders: Folder[]) => {
        for (const folder of folders) {
          if (folder.unsaved) folder.unsaved = false;
          if (folder.children) clearUnsaved(folder.children);
        }
      };

      clearUnsaved(state.folders);

      state.pendingChanges = [];

      // Hide reorder arrows if visible
      (state.ui as any).rootReorderFolderId = undefined;

      // Implementation for saving changes
    },
    cancelChanges(state) {
      // Helper function to find folder by ID in the tree
      const findFolder = (folders: BackendFolder[], id: number): BackendFolder | null => {
        for (const folder of folders) {
          if (folder.detailId === id) return folder;
          const found = findFolder(folder.children, id);

          if (found) return found;
        }

        return null;
      };

      // Reverse all pending operations before removing unsaved folders
      state.pendingChanges.forEach((change) => {
        if (change.type === "renameFolder") {
          // Revert rename operation - restore original name
          const folder = findFolder(state.folders, change.folderId);

          if (folder) {
            folder.folderName = change.oldName; // Restore original name
            folder.unsaved = false; // Remove unsaved flag
            // Note: Document folderPaths are restored via backend refetch in Toolbar.onDiscardChanges
          }
        } else if (change.type === "deleteFolder") {
          // Revert delete operation - restore folder and all children by removing deletion flag
          const folder = findFolder(state.folders, change.payload.folderId);

          if (folder) {
            const restoreDeleted = (folder: Folder) => {
              folder.deletionFlag = false;
              folder.unsaved = false;
              folder.children?.forEach(restoreDeleted); // Recursively restore ALL children
            };

            restoreDeleted(folder); // Restore folder and all nested children
          }
        }
        // addFolder/addSubfolder operations are handled by removing unsaved folders below
      });

      // Remove completely new folders (unsaved = true)
      const removedUnsaved = (folders: BackendFolder[]): BackendFolder[] => {
        return folders
          .filter((folder) => !folder.unsaved)
          .map((folder) => ({
            ...folder,
            children: folder.children ? removedUnsaved(folder.children) : [],
          }));
      };

      state.folders = removedUnsaved(state.folders);
      state.pendingChanges = [];

      // Clear any UI state
      state.ui.renamingFolderId = undefined;
      (state.ui as any).rootReorderFolderId = undefined;
    },
    setModal(
      state,
      action: PayloadAction<{
        showModal: boolean;
        modalType?: string;
        modalFolderId?: number;
      }>
    ) {
      state.ui = { ...state.ui, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInitialFoldersThunk.fulfilled, (state: DataRoomSetupFolderState, action) => {
        const rawFolders = action.payload as BackendFolder[];

        // Helper to check if folder OR ANY NESTED CHILDREN have active documents (not deleted)
        const hasActiveDocuments = (folder: BackendFolder): boolean => {
          // Check direct documents
          const hasDirectDocs =
            folder.documents &&
            folder.documents.length > 0 &&
            folder.documents.some((doc: any) => doc && doc.documentDeletionFlag !== true);

          if (hasDirectDocs) return true;

          // Recursively check children
          if (folder.children && folder.children.length > 0) {
            return folder.children.some((child) => hasActiveDocuments(child));
          }

          return false;
        };

        const filtered = rawFolders.filter((folder) => {
          const isRoot = folder.parentId === null;
          const isDefault = !!folder.systemPreferences;

          if (isRoot && isDefault) {
            // Always show if folder has active documents (direct or in nested children)
            if (hasActiveDocuments(folder)) return true;

            // Otherwise, follow the display rules from backend
            const shouldDisplay = folder.isDisplay === true;

            return shouldDisplay;
          }

          // Non-default or non-root folders are unaffected
          return true;
        });

        // Store complete raw data for validation and filtered data for display
        state.rawFolders = rawFolders;
        state.folders = filtered;
      })
      .addCase(postDataRoomsetFoldersThunk.fulfilled, (state: DataRoomSetupFolderState, action) => {
        // Helper to check if folder OR ANY NESTED CHILDREN have active documents (not deleted)
        const hasActiveDocuments = (folder: BackendFolder): boolean => {
          // Check direct documents
          const hasDirectDocs =
            folder.documents &&
            folder.documents.length > 0 &&
            folder.documents.some((doc: any) => doc && doc.documentDeletionFlag !== true);

          if (hasDirectDocs) return true;

          // Recursively check children
          if (folder.children && folder.children.length > 0) {
            return folder.children.some((child) => hasActiveDocuments(child));
          }

          return false;
        };

        const rawFolders = action.payload as BackendFolder[];

        const filtered = rawFolders.filter((folder) => {
          const isRoot = folder.parentId === null;
          const isDefault = !!folder.systemPreferences;

          if (isRoot && isDefault) {
            // Always show if folder has active documents (direct or in nested children)
            if (hasActiveDocuments(folder)) return true;

            // Otherwise follow the display rules from backend - display folders having isDisplay true
            const shouldDisplay = folder.isDisplay === true;

            return shouldDisplay;
          }

          return true;
        });

        state.rawFolders = rawFolders;
        state.folders = filtered;

        // Hide reorder arrows after successful save
        (state.ui as any).rootReorderFolderId = undefined;
      })
      .addCase(fetchInitialFoldersListThunk.fulfilled, (state, action) => {
        //state.loading = false;
        state.dataroomFolderList = action.payload;
      });
  },
});

export const {
  startRootReorder,
  stopRootReorder,
  moveRootUp,
  moveRootDown,
  addFolder,
  renameFolder,
  setRenamingFolder,
  deleteFolder,
  toggleExpandFolder,
  collapseAllFolders,
  selectFiles,
  incrementSelectionForFolder,
  decrementSelectionForFolder,
  clearSelection,
  moveSelectedFiles,
  assignFile,
  saveChanges,
  cancelChanges,
  setModal,
} = folderSlice.actions;
export default folderSlice.reducer;
